import { useMemo, useRef } from "react";

import { apiClient } from "@/core/api/ApiClient";
import {
  VehicleBodyType,
  VehicleType,
  VehicleVisualModelDto,
  VehicleVisualModelType,
} from "@/core/api/generated/v1";

import { useApiRequest } from "../../api/useApiRequest";

interface Props {
  /** @default true */
  isEnabled?: boolean;
  /** If not set, fetches default with the filters. */
  visualModelId: Nil<string>;
  /** @default true */
  isFallbackToDefault?: boolean;
  defaultModelFilters: {
    type?: VehicleVisualModelType | null;
    vehicleType?: VehicleType;
    bodyType?: VehicleBodyType | null;
    vehicleId?: string | null;
  };
}

interface HookResult {
  visualModel: Nil<VehicleVisualModelDto>;
  visualModelExact: Nil<VehicleVisualModelDto>;
  visualModelDefault: Nil<VehicleVisualModelDto>;
}

/** Returns the exact or the most suitable visual model. */
export function useExactOrDefaultVehicleVisualModel({
  isEnabled = true,
  visualModelId,
  isFallbackToDefault = true,
  defaultModelFilters,
}: Props): HookResult {
  const defaultVisualModelRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsLatestOrDefaultGet,
    {
      type: defaultModelFilters.type || undefined,
      vehicleType: defaultModelFilters.vehicleType || undefined,
      bodyType: defaultModelFilters.bodyType || undefined,
      vehicleId: defaultModelFilters.vehicleId || undefined,
    },
    {
      deps: [
        isEnabled,
        isFallbackToDefault,
        visualModelId,
        defaultModelFilters.type,
        defaultModelFilters.vehicleType,
        defaultModelFilters.bodyType,
        defaultModelFilters.vehicleId,
      ],
      skip: !isEnabled || !isFallbackToDefault || !!visualModelId,
    },
  );
  const defaultVisualModel = defaultVisualModelRequest?.data;

  const exactVisualModelRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsModelIdGet,
    {
      modelId: visualModelId!,
    },
    {
      deps: [isEnabled, visualModelId],
      skip: !isEnabled || !visualModelId,
    },
  );
  const exactVisualModel = exactVisualModelRequest?.data;

  const getResult = (): HookResult => ({
    visualModel: visualModelId ? exactVisualModel : defaultVisualModel,
    visualModelExact: exactVisualModel,
    visualModelDefault: defaultVisualModel,
  });
  const initialResult = useMemo(() => getResult(), []);
  const resultRef = useRef<HookResult>(initialResult);
  Object.assign(resultRef.current, getResult());

  return resultRef.current;
}
